<template>
  <v-dialog
      v-model="dialogVisible"
      persistent
      max-width="98vw"
      content-class="activity-monitor-planned-dialog"
  >
    <v-card class="planned-dialog__card">
      <v-card-title class="planned-dialog__title">
        <div>
          <div class="text-h6">{{ title }}</div>
          <div class="planned-dialog__subtitle">{{ period?.name }}</div>
        </div>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="planned-dialog__body">
        <LoaderDialog :show="loading" msg="Загрузка..." />

        <div v-if="!loading && isEmpty" class="planned-dialog__empty">
          {{ emptyMessage }}
        </div>

        <div v-else-if="!loading" ref="tableWrap" class="planned-table-wrap">
          <table
              class="planned-table"
              :class="{ 'planned-table--czn': mode === 'czn' }"
              :style="{ '--col-count': columns.length }"
          >
            <colgroup>
              <col class="planned-table__col-row" />
              <col
                  v-for="indicator in columns"
                  :key="'col-' + indicator.id"
                  class="planned-table__col-data"
              />
            </colgroup>
            <thead>
            <tr>
              <th class="planned-table__row-header planned-table__row-header-label">{{ rowHeaderLabel }}</th>
              <th
                  v-for="indicator in columns"
                  :key="indicator.id"
                  class="planned-table__col-header"
              >
                <div class="planned-table__col-code">{{ indicator.planned.code }}</div>
                <div class="planned-table__col-name" :title="indicator.name">{{ indicator.name }}</div>
                <div class="planned-table__col-unit">{{ indicator.unit }}</div>
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="row in rows" :key="row.key">
              <th class="planned-table__row-header">
                <span class="planned-table__row-name" :title="row.name">{{ row.name }}</span>
              </th>
              <td v-for="indicator in columns" :key="indicator.id + '_' + row.key">
                <v-text-field
                    :value="getCellValue(row, indicator)"
                    @input="setCellValue(row, indicator, $event)"
                    @keydown.native="onDecimalKeydown"
                    type="text"
                    inputmode="decimal"
                    dense
                    hide-details
                    placeholder="—"
                    :disabled="isPeriodReadonly || !row.editable"
                    class="planned-table__input"
                />
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </v-card-text>

      <v-card-actions class="planned-dialog__actions">
        <v-spacer />
        <v-btn class="btn btn-outline planned-dialog__btn" text small @click="close">Отмена</v-btn>
        <v-btn
            v-if="hasEditableRows"
            class="btn btn-primary planned-dialog__btn"
            small
            :disabled="isPeriodReadonly || saving"
            :loading="saving"
            @click="save"
        >
          Сохранить
        </v-btn>
      </v-card-actions>

      <v-snackbar v-model="snackbar" :timeout="2000" :color="snackbarError ? 'red' : 'green'" text centered>
        {{ snackbarText }}
      </v-snackbar>
    </v-card>
  </v-dialog>
</template>

<script>
import LoaderDialog from "../elements/LoaderDialog.vue";
import {getIndicatorsByDomainAndPeriod, saveIndicators} from "@/modules/api.indicators";
import {FULL_ACCESS_ROLES, REGIONAL_ROLES, TERRITORIAL_ROLES} from "@/modules/ActivityMonitorRoles";
import {filterMonitorRegions, isMonitorRegion, hasRegionRating, hasCznRating} from "@/modules/ActivityMonitorRatingRegions";

const DOMAIN_PLANNED = 'activity_monitor_indicators_planned';

export default {
  name: 'ActivityMonitorPlannedDialog',
  components: {LoaderDialog},
  props: {
    value: Boolean,
    mode: {
      type: String,
      required: true,
      validator: v => ['region', 'czn'].includes(v),
    },
    period: Object,
    configIndicators: Array,
    regions: Array,
    regionAndDepartmentInfo: Object,
  },
  data: () => ({
    loading: false,
    saving: false,
    values: [],
    snackbar: false,
    snackbarText: '',
    snackbarError: false,
  }),
  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    title() {
      if (this.mode === 'region') {
        return 'Плановые показатели по регионам';
      }
      const regionName = this.regionAndDepartmentInfo?.region?.name;
      return regionName
          ? `Плановые показатели по ЦЗН (${regionName})`
          : 'Плановые показатели по ЦЗН';
    },
    isEmpty() {
      return !this.columns.length || !this.rows.length;
    },
    emptyMessage() {
      if (!this.columns.length) {
        return 'Нет показателей с плановыми значениями';
      }
      return 'Нет данных для отображения';
    },
    rowHeaderLabel() {
      return this.mode === 'region' ? 'Субъект РФ' : 'ЦЗН';
    },
    columns() {
      if (!this.configIndicators) return [];
      return this.configIndicators
          .filter(i => i?.planned?.id)
          .filter(i => this.mode === 'region'
              ? hasRegionRating(i)
              : (!i.isOnlyRegional && hasCznRating(i)));
    },
    rows() {
      const role = this.$user.role;

      if (this.mode === 'region') {
        return this.getRegionPlannedRows(role);
      }

      return this.getCznPlannedRows(role);
    },
    hasEditableRows() {
      return this.rows.some(row => row.editable);
    },
    isPeriodReadonly() {
      // Плановые значения можно менять независимо от статуса периода
      return false;
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.loadValues();
      }
    },
  },
  methods: {
    getRegionPlannedRows(role) {
      const regionRows = filterMonitorRegions(this.regions || []).map(region => ({
        key: region.code,
        name: region.name,
        regionCode: region.code,
        cznId: null,
        editable: FULL_ACCESS_ROLES.includes(role),
      }));

      if (FULL_ACCESS_ROLES.includes(role)) {
        return regionRows;
      }

      if (REGIONAL_ROLES.includes(role)) {
        const ownRegionCode = this.$user.region.code;
        return regionRows.filter(row => row.regionCode === ownRegionCode);
      }

      return [];
    },
    getCznPlannedRows(role) {
      const region = this.regionAndDepartmentInfo?.region;
      if (!region || !isMonitorRegion(region.code)) return [];

      const departmentRows = (region.departments || [])
          .filter(d => d.type === 'territorial_czn')
          .map(department => ({
            key: String(department.id),
            name: department.name,
            regionCode: region.code,
            cznId: department.id,
            editable: FULL_ACCESS_ROLES.includes(role) || REGIONAL_ROLES.includes(role),
          }));

      if (FULL_ACCESS_ROLES.includes(role) || REGIONAL_ROLES.includes(role)) {
        return departmentRows;
      }

      if (TERRITORIAL_ROLES.includes(role)) {
        const ownCznId = this.$user.department.id;
        return departmentRows
            .filter(row => row.cznId === ownCznId)
            .map(row => ({...row, editable: false}));
      }

      return [];
    },
    close() {
      this.dialogVisible = false;
    },
    findValue(row, indicator) {
      return this.values.find(v =>
          v.indicatorId === indicator.planned.id &&
          v.regionCode === row.regionCode &&
          (row.cznId == null ? v.cznId == null : v.cznId === row.cznId)
      );
    },
    getCellValue(row, indicator) {
      const value = this.findValue(row, indicator)?.value;
      return value == null || value === '' ? null : value;
    },
    getOrCreateValue(row, indicator) {
      let existing = this.findValue(row, indicator);
      if (!existing) {
        existing = {
          indicatorId: indicator.planned.id,
          regionCode: row.regionCode,
          cznId: row.cznId,
          period: this.period.code,
          type: 'VALUE',
          value: null,
        };
        this.values.push(existing);
      }
      return existing;
    },
    setCellValue(row, indicator, value) {
      if (this.isPeriodReadonly || !row.editable) return;

      const entry = this.getOrCreateValue(row, indicator);
      entry.value = this.sanitizeDecimalInput(value);
    },
    sanitizeDecimalInput(value) {
      if (value === '' || value == null) return null;

      let str = String(value).replace(/,/g, '.').replace(/[^\d.]/g, '');
      const firstDot = str.indexOf('.');
      if (firstDot !== -1) {
        str = str.slice(0, firstDot + 1) + str.slice(firstDot + 1).replace(/\./g, '');
      }

      return str === '' ? null : str;
    },
    onDecimalKeydown(event) {
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (allowedKeys.includes(event.key)) return;
      if (/^\d$/.test(event.key)) return;

      const input = event.target;
      const currentValue = input?.value ?? '';
      if ((event.key === '.' || event.key === ',') && !currentValue.includes('.') && !currentValue.includes(',')) {
        return;
      }

      event.preventDefault();
    },
    async loadValues() {
      if (!this.period?.code || !this.columns.length) {
        this.values = [];
        return;
      }

      this.loading = true;
      try {
        const cznMode = this.mode === 'region' ? 'EMPTY' : 'FILLED';
        const response = await getIndicatorsByDomainAndPeriod(DOMAIN_PLANNED, this.period.code, cznMode);
        let loaded = response.payload || [];

        if (this.mode === 'czn') {
          const regionCode = this.regionAndDepartmentInfo?.region?.code;
          loaded = loaded.filter(v => v.regionCode === regionCode);
        }

        const plannedIds = new Set(this.columns.map(c => c.planned.id));
        this.values = loaded.filter(v =>
            plannedIds.has(v.indicatorId) &&
            this.rows.some(row =>
                v.regionCode === row.regionCode &&
                (row.cznId == null ? v.cznId == null : v.cznId === row.cznId)
            )
        );
      } catch (e) {
        console.error(e);
        this.showSnackbar('Ошибка загрузки данных', true);
        this.values = [];
      } finally {
        this.loading = false;
      }
    },
    async save() {
      if (this.isPeriodReadonly) return;

      this.saving = true;
      try {
        const toSave = this.values.filter(v => {
          if (v.value == null || v.value === '') return false;
          return this.rows.some(row =>
              row.editable &&
              v.regionCode === row.regionCode &&
              (row.cznId == null ? v.cznId == null : v.cznId === row.cznId)
          );
        });
        await saveIndicators(toSave);
        this.showSnackbar('Сохранено');
        this.$emit('saved');
        this.close();
      } catch (e) {
        console.error(e);
        this.showSnackbar('Ошибка сохранения', true);
      } finally {
        this.saving = false;
      }
    },
    showSnackbar(text, error = false) {
      this.snackbarText = text;
      this.snackbarError = error;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../styles/main.scss";

.planned-dialog__title {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: 1px solid #e8e8e9;
  flex-shrink: 0;
}

.planned-dialog__subtitle {
  font-size: 14px;
  color: #777779;
  font-weight: 400;
  margin-top: 4px;
}

.planned-dialog__body {
  padding: 0 !important;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.planned-dialog__empty {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  color: #777779;
  text-align: center;
  line-height: 1.6;
}

.planned-table-wrap {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

.planned-table {
  table-layout: fixed;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;

  &__col-row {
    width: 10%;
  }

  &__col-data {
    width: calc(90% / var(--col-count, 1));
  }

  th, td {
    border-bottom: 1px solid #e8e8e9;
    border-right: 1px solid #e8e8e9;
    padding: 4px 6px;
    vertical-align: middle;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f5f7fb;
  }

  &__row-header {
    width: 10%;
    min-width: 0;
    background: #fff;
    text-align: left;
    font-weight: 500;
    line-height: 1.25;
  }

  thead &__row-header {
    background: #f5f7fb;
    font-size: 12px;
  }

  &__row-header-label {
    font-weight: 600;
    color: $cblue;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  &__row-name {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-size: 12px;
    word-break: break-word;
  }

  &--czn {
    .planned-table__col-row {
      width: 22%;
    }

    .planned-table__col-data {
      width: calc(78% / var(--col-count, 1));
    }

    .planned-table__row-header {
      width: 22%;
    }

    .planned-table__row-name {
      -webkit-line-clamp: 4;
      line-height: 1.3;
    }
  }

  &__col-header {
    min-width: 0;
    text-align: center;
    vertical-align: bottom;
  }

  &__col-code {
    font-weight: 600;
    font-size: 12px;
    color: $cblue;
    margin-bottom: 2px;
    white-space: nowrap;
  }

  &__col-name {
    font-size: 10px;
    line-height: 1.3;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    word-break: break-word;
  }

  &__col-unit {
    font-size: 10px;
    color: #777779;
    margin-top: 2px;
    white-space: nowrap;
  }

  &__input {
    width: 100%;
    margin: 0;

    ::v-deep .v-input__control {
      min-height: 32px !important;
    }

    ::v-deep .v-input__slot {
      padding: 0 4px !important;
      min-height: 32px !important;
    }

    ::v-deep input {
      font-size: 13px;
      text-align: center;
      padding: 0 2px !important;
    }
  }
}

@media (max-width: 1280px) {
  .planned-table {
    th, td {
      padding: 3px 4px;
    }

    &__row-name {
      font-size: 11px;
      -webkit-line-clamp: 3;
    }

    .planned-table--czn .planned-table__row-name {
      -webkit-line-clamp: 4;
    }

    &__col-code {
      font-size: 11px;
    }

    &__col-name {
      font-size: 9px;
      -webkit-line-clamp: 4;
    }

    &__input ::v-deep input {
      font-size: 12px;
    }
  }
}

.planned-dialog__actions {
  flex-shrink: 0;
  border-top: 1px solid #e8e8e9;
  padding: 10px 16px !important;
  min-height: unset !important;
  gap: 8px;

  ::v-deep .planned-dialog__btn.v-btn {
    height: 34px !important;
    min-width: 0 !important;
    padding: 0 18px !important;
    font-size: 12px !important;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
  }
}
</style>

<style lang="scss">
.activity-monitor-planned-dialog {
  margin: 12px;
  height: calc(100vh - 24px);
  max-height: calc(100vh - 24px);
  overflow: hidden;
}

.planned-dialog__card {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  overflow: hidden;

  > .v-card__text {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .planned-dialog__actions .planned-dialog__btn.btn {
    height: 34px !important;
    min-height: 34px !important;
    padding: 0 18px !important;
    font-size: 12px !important;
  }
}
</style>
